export interface ReportPatient {
  patient_id?: string;
  _id?: string;
  patient_name?: string;
  patient_phone?: string;
  normalized_phone?: string;
  file_number?: string;
  file_id?: string;
  doctor_id?: string;
  doctor_name?: string;
  visits?: any[];
  all_visits?: any[];
}

export interface VisitReportRow {
  key: string;
  visitId: string;
  visitDate: string;
  visitTime: string;
  visitType: string;
  doctorId: string;
  doctorName: string;
  patientKey: string;
  patientName: string;
  patientPhone: string;
  fileNumber: string;
}

export interface DoctorAttendanceSession {
  key: string;
  doctorId: string;
  doctorName: string;
  clinicName: string;
  date: string;
  signInAt: string;
  signOutAt: string | null;
  minutes: number | null;
}

export interface VisitAttendanceRow extends VisitReportRow {
  attendanceStatus: 'present' | 'missing';
  signInTime: string;
  signOutTime: string;
  workedMinutes: number | null;
}

export interface DoctorDailySummaryRow {
  key: string;
  doctorId: string;
  doctorName: string;
  clinicName: string;
  date: string;
  casesCount: number;
  attendanceStatus: 'present' | 'missing';
  signInTime: string;
  signOutTime: string;
  workedMinutes: number | null;
  attendanceSessions: number;
}

export interface DoctorDailyTotals {
  casesCount: number;
  attendanceDays: number;
  workedMinutes: number;
}

export interface DoctorDailyExportRow {
  Clinic: string;
  Date: string;
  Doctor: string;
  'Cases Count': number;
  'Doctor Attendance': 'Present' | 'No attendance record';
  'Check In': string;
  'Check Out': string;
  'Worked Hours': number | '';
}

export interface PatientFrequencyRow {
  key: string;
  patientName: string;
  patientPhone: string;
  fileNumber: string;
  visitsCount: number;
  firstVisit: string;
  lastVisit: string;
  doctorNames: string[];
}

export function filterReportRowsByDoctor<T extends { doctorId: string }>(
  rows: T[],
  doctorId: string,
): T[] {
  if (!doctorId) return rows;
  return rows.filter((row) => row.doctorId === doctorId);
}

const text = (value: unknown): string => String(value ?? '').trim();

export function reportDateKey(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const date = new Date(value as any);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function patientIdentity(patient: ReportPatient): string {
  const fileNumber = text(patient.file_number || patient.file_id);
  const phone = text(patient.normalized_phone || patient.patient_phone).replace(/\D/g, '');
  const patientId = text(patient.patient_id || patient._id);
  if (fileNumber) return `file:${fileNumber}|phone:${phone || patientId}`;
  if (phone) return `phone:${phone}|name:${text(patient.patient_name).toLocaleLowerCase()}`;
  return `id:${patientId || text(patient.patient_name).toLocaleLowerCase()}`;
}

function visitIdentity(visit: any, patientKey: string, doctorId: string): string {
  const explicitId = text(visit?.visit_id || visit?._id);
  if (explicitId) return `visit:${explicitId}`;
  return [
    'legacy', patientKey, doctorId, reportDateKey(visit?.date),
    text(visit?.time), text(visit?.visit_type),
  ].join('|');
}

export function buildVisitReportRows(
  patients: ReportPatient[],
  doctorNames: Record<string, string>,
  startDate: string,
  endDate: string,
  visitTypeLabels: Record<string, string> = {},
): VisitReportRow[] {
  const rows: VisitReportRow[] = [];
  const seenVisits = new Set<string>();

  for (const patient of Array.isArray(patients) ? patients : []) {
    const patientKey = patientIdentity(patient);
    const visits = [...(patient.all_visits || []), ...(patient.visits || [])];
    for (const visit of visits) {
      if (!visit || visit.deletedAt) continue;
      const visitDate = reportDateKey(visit.date);
      if (!visitDate || visitDate < startDate || visitDate > endDate) continue;

      const doctorId = text(visit.owner_doctor_id || visit.doctor_id || patient.doctor_id);
      const visitKey = visitIdentity(visit, patientKey, doctorId);
      if (seenVisits.has(visitKey)) continue;
      seenVisits.add(visitKey);

      const visitId = text(visit.visit_id || visit._id);
      rows.push({
        key: visitKey,
        visitId,
        visitDate,
        visitTime: text(visit.time),
        visitType: visitTypeLabels[text(visit.visit_type)] || text(visit.visit_type) || '—',
        doctorId,
        doctorName: text(visit.owner_doctor_name || visit.doctor_name)
          || doctorNames[doctorId]
          || text(patient.doctor_name)
          || doctorId,
        patientKey,
        patientName: text(patient.patient_name) || '—',
        patientPhone: text(patient.patient_phone),
        fileNumber: text(patient.file_number || patient.file_id),
      });
    }
  }

  return rows.sort((a, b) => {
    const dateOrder = b.visitDate.localeCompare(a.visitDate);
    if (dateOrder !== 0) return dateOrder;
    return b.visitTime.localeCompare(a.visitTime);
  });
}

export function pairDoctorAttendanceLogs(
  logs: any[],
  doctorNames: Record<string, string>,
): DoctorAttendanceSession[] {
  const grouped = new Map<string, any[]>();
  for (const log of Array.isArray(logs) ? logs : []) {
    const doctorId = text(log?.doctor_id);
    if (!doctorId || !log?.createdAt) continue;
    const items = grouped.get(doctorId) || [];
    items.push(log);
    grouped.set(doctorId, items);
  }

  const sessions: DoctorAttendanceSession[] = [];
  grouped.forEach((items, doctorId) => {
    const sorted = items.slice().sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    let pending: any = null;

    const appendSession = (signIn: any, signOut: any | null) => {
      const start = new Date(signIn.createdAt);
      const end = signOut ? new Date(signOut.createdAt) : null;
      const validEnd = end && !Number.isNaN(end.getTime()) ? end : null;
      sessions.push({
        key: text(signIn._id || signIn.request_id) || `${doctorId}-${start.toISOString()}`,
        doctorId,
        doctorName: text(signIn.doctor_name || signOut?.doctor_name) || doctorNames[doctorId] || doctorId,
        clinicName: text(signIn.clinic_name || signOut?.clinic_name),
        date: reportDateKey(signIn.createdAt),
        signInAt: signIn.createdAt,
        signOutAt: validEnd ? signOut.createdAt : null,
        minutes: validEnd ? Math.max(0, Math.round((validEnd.getTime() - start.getTime()) / 60000)) : null,
      });
    };

    for (const log of sorted) {
      if (log.action === 'sign_in') {
        if (pending) appendSession(pending, null);
        pending = log;
      } else if (log.action === 'sign_out' && pending) {
        // Never let a forgotten check-out create a multi-day "worked" session.
        // Clinics in this report operate in daily shifts, so a check-out from a
        // later date is orphaned and the original check-in remains open.
        if (reportDateKey(pending.createdAt) === reportDateKey(log.createdAt)) {
          appendSession(pending, log);
        } else {
          appendSession(pending, null);
        }
        pending = null;
      }
    }
    if (pending) appendSession(pending, null);
  });

  return sessions.sort((a, b) => new Date(b.signInAt).getTime() - new Date(a.signInAt).getTime());
}

function formatTime(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function attachDoctorAttendance(
  visits: VisitReportRow[],
  sessions: DoctorAttendanceSession[],
): VisitAttendanceRow[] {
  const byDoctorDay = new Map<string, DoctorAttendanceSession[]>();
  for (const session of sessions) {
    const key = `${session.doctorId}|${session.date}`;
    const items = byDoctorDay.get(key) || [];
    items.push(session);
    byDoctorDay.set(key, items);
  }

  return visits.map((visit) => {
    const daySessions = byDoctorDay.get(`${visit.doctorId}|${visit.visitDate}`) || [];
    if (daySessions.length === 0) {
      return {
        ...visit,
        attendanceStatus: 'missing',
        signInTime: '',
        signOutTime: '',
        workedMinutes: null,
      };
    }
    const sorted = daySessions.slice().sort(
      (a, b) => new Date(a.signInAt).getTime() - new Date(b.signInAt).getTime()
    );
    const hasOpenSession = sorted.some((session) => !session.signOutAt);
    const totalMinutes = sorted.reduce((sum, session) => sum + (session.minutes || 0), 0);
    return {
      ...visit,
      attendanceStatus: 'present',
      signInTime: formatTime(sorted[0].signInAt),
      signOutTime: hasOpenSession ? 'Still checked in' : formatTime(sorted[sorted.length - 1].signOutAt),
      workedMinutes: totalMinutes,
    };
  });
}

/**
 * Build the owner-facing operational summary: one row per doctor and day.
 * The union of visits and attendance sessions is used deliberately so that a
 * doctor who attended but saw no patients still appears with Cases = 0, while
 * a doctor with visits but no attendance log is shown as missing attendance.
 */
export function buildDoctorDailySummaryRows(
  visits: VisitReportRow[],
  sessions: DoctorAttendanceSession[],
): DoctorDailySummaryRow[] {
  type MutableDailyRow = DoctorDailySummaryRow & {
    sessionItems: DoctorAttendanceSession[];
  };
  const grouped = new Map<string, MutableDailyRow>();

  const ensureRow = (
    doctorId: string,
    date: string,
    doctorName = '',
    clinicName = '',
  ): MutableDailyRow => {
    const key = `${doctorId}|${date}`;
    const existing = grouped.get(key);
    if (existing) {
      if ((!existing.doctorName || existing.doctorName === existing.doctorId) && doctorName) {
        existing.doctorName = doctorName;
      }
      if (!existing.clinicName && clinicName) existing.clinicName = clinicName;
      return existing;
    }
    const row: MutableDailyRow = {
      key,
      doctorId,
      doctorName: doctorName || doctorId,
      clinicName,
      date,
      casesCount: 0,
      attendanceStatus: 'missing',
      signInTime: '',
      signOutTime: '',
      workedMinutes: null,
      attendanceSessions: 0,
      sessionItems: [],
    };
    grouped.set(key, row);
    return row;
  };

  for (const visit of visits) {
    if (!visit.doctorId || !visit.visitDate) continue;
    const row = ensureRow(visit.doctorId, visit.visitDate, visit.doctorName);
    row.casesCount += 1;
  }

  for (const session of sessions) {
    if (!session.doctorId || !session.date) continue;
    const row = ensureRow(
      session.doctorId,
      session.date,
      session.doctorName,
      session.clinicName,
    );
    row.sessionItems.push(session);
  }

  return Array.from(grouped.values())
    .map(({ sessionItems, ...row }) => {
      if (sessionItems.length === 0) return row;
      const sorted = sessionItems.slice().sort(
        (a, b) => new Date(a.signInAt).getTime() - new Date(b.signInAt).getTime()
      );
      const hasOpenSession = sorted.some((session) => !session.signOutAt);
      const totalMinutes = sorted.reduce((sum, session) => sum + (session.minutes || 0), 0);
      return {
        ...row,
        doctorName: row.doctorName || sorted[0].doctorName || row.doctorId,
        clinicName: row.clinicName || sorted[0].clinicName,
        attendanceStatus: 'present' as const,
        signInTime: formatTime(sorted[0].signInAt),
        signOutTime: hasOpenSession
          ? 'Still checked in'
          : formatTime(sorted[sorted.length - 1].signOutAt),
        workedMinutes: totalMinutes,
        attendanceSessions: sorted.length,
      };
    })
    .sort((a, b) => {
      const dateOrder = b.date.localeCompare(a.date);
      if (dateOrder !== 0) return dateOrder;
      return a.doctorName.localeCompare(b.doctorName);
    });
}

/** Totals for the complete filtered period (not only the visible page). */
export function buildDoctorDailyTotals(rows: DoctorDailySummaryRow[]): DoctorDailyTotals {
  return rows.reduce<DoctorDailyTotals>((totals, row) => ({
    casesCount: totals.casesCount + row.casesCount,
    attendanceDays: totals.attendanceDays + (row.attendanceStatus === 'present' ? 1 : 0),
    workedMinutes: totals.workedMinutes + (row.workedMinutes || 0),
  }), {
    casesCount: 0,
    attendanceDays: 0,
    workedMinutes: 0,
  });
}

/** Keep the Excel contract in a pure, regression-testable function. */
export function buildDoctorDailyExportRows(
  rows: DoctorDailySummaryRow[],
  clinicName: string,
): DoctorDailyExportRow[] {
  return rows.map((row) => ({
    Clinic: clinicName,
    Date: row.date,
    Doctor: row.doctorName,
    'Cases Count': row.casesCount,
    'Doctor Attendance': row.attendanceStatus === 'present' ? 'Present' : 'No attendance record',
    'Check In': row.signInTime,
    'Check Out': row.signOutTime,
    'Worked Hours': row.workedMinutes == null
      ? ''
      : Number((row.workedMinutes / 60).toFixed(2)),
  }));
}

export function buildPatientFrequencyRows(visits: VisitReportRow[]): PatientFrequencyRow[] {
  const grouped = new Map<string, PatientFrequencyRow & { doctorSet: Set<string> }>();
  for (const visit of visits) {
    const existing = grouped.get(visit.patientKey) || {
      key: visit.patientKey,
      patientName: visit.patientName,
      patientPhone: visit.patientPhone,
      fileNumber: visit.fileNumber,
      visitsCount: 0,
      firstVisit: visit.visitDate,
      lastVisit: visit.visitDate,
      doctorNames: [],
      doctorSet: new Set<string>(),
    };
    existing.visitsCount += 1;
    if (visit.visitDate < existing.firstVisit) existing.firstVisit = visit.visitDate;
    if (visit.visitDate > existing.lastVisit) existing.lastVisit = visit.visitDate;
    if (visit.doctorName) existing.doctorSet.add(visit.doctorName);
    grouped.set(visit.patientKey, existing);
  }

  return Array.from(grouped.values())
    .map(({ doctorSet, ...row }) => ({ ...row, doctorNames: Array.from(doctorSet).sort() }))
    .sort((a, b) => b.visitsCount - a.visitsCount || a.patientName.localeCompare(b.patientName));
}
